import React, { useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, MessageSquare, ClipboardCheck, FileText, HardHat } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../api/services";
import { useAbout, usePageBanner } from "../api/queries";
import { splitContactNumbers } from "../lib/utils";
import { type } from "../lib/typography";

const ENQUIRY_STEPS = [
  {
    icon: MessageSquare,
    title: "Share Your Requirements",
    desc: "Tell us about your site, scope and timeline through the form or a direct call.",
  },
  {
    icon: ClipboardCheck,
    title: "Site Assessment",
    desc: "Our engineers review your needs and schedule a consultation or site visit.",
  },
  {
    icon: FileText,
    title: "Tailored Proposal",
    desc: "Receive a detailed technical proposal with scope, methodology and timelines.",
  },
  {
    icon: HardHat,
    title: "Project Delivery",
    desc: "Mobilise our nationwide team for execution with quality and safety at every stage.",
  },
];

export default function ContactPage() {
  const { toast } = useToast();
  const { data: companyInfo = { phone: "", mobile: "", email: "", timing: "", address: "", experience: "", professionals: "", branches: "", projects: "" } } = useAbout();
  const phoneNumbers = useMemo(() => {
    const numbers = [
      ...splitContactNumbers(companyInfo.phone),
      ...splitContactNumbers(companyInfo.mobile),
    ];
    return [...new Set(numbers)];
  }, [companyInfo.phone, companyInfo.mobile]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const { banner: contactBanner, isLoading: bannerLoading } = usePageBanner("contact_us");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitContact(form);
      setSubmitted(true);
      toast({
        title: "Message received",
        description: "Our team will get back to you within 24 hours.",
      });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      toast({
        title: "Submission failed",
        description: "Please try again or email us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <PageHeader
        title={
          companyInfo.contactHeader?.title ||
          contactBanner?.title ||
          "Get in Touch"
        }
        breadcrumb={companyInfo.contactHeader?.title || "Contact"}
        image={contactBanner?.image}
        isLoading={bannerLoading}
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-4">
            <SectionTitle
              eyebrow={companyInfo.contactHeader?.tagline || "Reach Us"}
              title="Let's Connect"
            />
            <p className="text-slate-600 leading-relaxed">
              Whether it's a structural assessment, a turnkey civil project or a waterproofing query — our team is ready to help.
            </p>

            <div className="space-y-4 mt-8">
              <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#1e6fcf] transition-colors">
                <div className="w-11 h-11 rounded-md bg-[#1e6fcf] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Head Office</div>
                  <div className="text-[#0a1a2f] font-semibold mt-1">{companyInfo.address || "New Delhi, India"}</div>
                  <div className="text-sm text-slate-600 mt-1">
                    {companyInfo.branches ? `${companyInfo.branches} clients across India` : "Trusted by leading organisations"}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#1e6fcf] transition-colors">
                <div className="w-11 h-11 rounded-md bg-[#1e6fcf] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Phone</div>
                  {phoneNumbers.length > 0 && (
                    <p className="mt-1 text-[#0a1a2f] font-semibold leading-relaxed">
                      {phoneNumbers.map((number, index) => (
                        <React.Fragment key={number}>
                          {index > 0 && <span className="text-slate-400">, </span>}
                          <a href={`tel:${number}`} className="hover:text-[#1758a8] whitespace-nowrap">
                            {number}
                          </a>
                        </React.Fragment>
                      ))}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#1e6fcf] transition-colors">
                <div className="w-11 h-11 rounded-md bg-[#1e6fcf] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Email</div>
                  <a href={`mailto:${companyInfo.email}`} className="block text-[#0a1a2f] font-semibold mt-1 hover:text-[#1758a8]">
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#1e6fcf] transition-colors">
                <div className="w-11 h-11 rounded-md bg-[#1e6fcf] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Working Hours</div>
                  <div className="text-[#0a1a2f] font-semibold mt-1">{companyInfo.timing || "Mon – Sat: 9:00 AM – 6:30 PM"}</div>
                  <div className="text-sm text-slate-600 mt-0.5">Sunday: Closed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 md:p-10">
              <h2 className={`${type.sectionTitle} text-[#0a1a2f]`}>
                Send us a message
              </h2>
              <p className="text-slate-600 mt-2">Fill the form below and our team will respond within 24 hours.</p>

              <form onSubmit={onSubmit} className="mt-8 grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Full Name *</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Email *</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                    placeholder="you@example.com"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Phone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                    placeholder="+91 9XXXX XXXXX"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Subject *</label>
                  <input
                    required
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all"
                    placeholder="Project enquiry / Service"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Message *</label>
                  <textarea
                    required
                    rows={5}
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    className="mt-2 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your project requirements..."
                  />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  {submitted ? (
                    <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                      <CheckCircle2 className="w-5 h-5" />
                      Thank you! We'll be in touch shortly.
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">* Required fields. Your information is kept confidential.</p>
                  )}
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-[#0a1a2f] shadow-sm transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white"
                  >
                    Send Message
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle
            align="center"
            eyebrow="How It Works"
            title="From Enquiry to On-Site Delivery"
            subtitle="A clear, engineering-led journey — designed to move your project forward with confidence."
          />

          <div className="mt-10 md:mt-12 flex flex-wrap justify-center gap-2 sm:gap-3">
            {[
              companyInfo.experience && `${companyInfo.experience} Years of Excellence`,
              companyInfo.projects && `${companyInfo.projects} Projects Delivered`,
              companyInfo.professionals && `${companyInfo.professionals} Skilled Professionals`,
              companyInfo.branches && `${companyInfo.branches} Clients Nationwide`,
            ]
              .filter(Boolean)
              .map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0a1a2f] shadow-sm sm:px-4 sm:py-2 sm:text-sm"
                >
                  {label}
                </span>
              ))}
          </div>

          <div className="mt-12 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {ENQUIRY_STEPS.map((step, index) => (
              <div key={step.title} className="relative group">
                {index < ENQUIRY_STEPS.length - 1 && (
                  <div
                    className="hidden lg:block absolute top-12 left-[calc(50%+2.5rem)] w-[calc(100%-2rem)] h-px bg-gradient-to-r from-[#1e6fcf]/50 to-[#1e6fcf]/10"
                    aria-hidden="true"
                  />
                )}
                <div className="relative h-full p-6 bg-white rounded-xl border border-slate-200 hover:border-[#1e6fcf] hover:shadow-xl transition-all duration-300">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#1e6fcf]">
                    Step {String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-4 w-14 h-14 rounded-lg bg-[#1e6fcf]/10 group-hover:bg-[#1e6fcf] flex items-center justify-center transition-colors duration-300">
                    <step.icon className="w-7 h-7 text-[#1e6fcf] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className={`mt-5 ${type.subsectionTitle} text-[#0a1a2f]`}>{step.title}</h3>
                  <p className={`mt-2 ${type.bodySm}`}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
